// ─────────────────────────────────────────────────────────────────────────────
// MICROSOFT CLARITY — session analytics for the production console only.
//
// Two gates must both pass before the Clarity script is loaded:
//   1. Production host: only app/console/portal.xenquote.com. Dev
//      (*.dev.xenquote.com), localhost and anything else never load it.
//   2. Analytics consent: the shared banner (https://xenquote.com/xq-consent.js)
//      stores the visitor's choice in the `xq_consent` cookie on .xenquote.com
//      as "a<0|1>m<0|1>" (a1 = analytics accepted, m1 = marketing accepted) and
//      fires a window "xq-consent" CustomEvent every time the choice changes.
//
// No identify() or setTag() calls are made: nothing personal (emails, names,
// company ids) is ever sent to Clarity.
// ─────────────────────────────────────────────────────────────────────────────
import Clarity from "@microsoft/clarity";

// Clarity project for the xenquote console
const CLARITY_PROJECT_ID = "yogrnkwx9n";

// Exact hostnames that count as production; everything else is ignored
const PROD_HOSTS = new Set(["app.xenquote.com", "console.xenquote.com", "portal.xenquote.com"]);

// Name of the shared consent cookie written by xq-consent.js
const CONSENT_COOKIE = "xq_consent";

// The visitor's consent choice, per category
type Consent = { analytics: boolean; marketing: boolean };

// Shape of the "xq-consent" event detail dispatched by the banner script
type ConsentEventDetail = Partial<Consent>;

// Tracks whether Clarity.init() has already injected the script on this page
let started = false;

// Read the xq_consent cookie; returns null when the visitor hasn't chosen yet
// (or the value is malformed), which we treat the same as "not accepted".
function readConsentCookie(): Consent | null {
  const entry = document.cookie
    .split(";")
    .map((part) => part.trim())
    .find((part) => part.startsWith(`${CONSENT_COOKIE}=`));
  if (!entry) return null;

  const value = decodeURIComponent(entry.slice(CONSENT_COOKIE.length + 1));
  const match = /^a([01])m([01])$/.exec(value);
  if (!match) return null;

  return { analytics: match[1] === "1", marketing: match[2] === "1" };
}

// Tell Clarity which storage categories the visitor allows. Clarity's
// consentV2 maps analytics → analytics_Storage and marketing → ad_Storage.
function sendConsent(consent: Consent): void {
  Clarity.consentV2({
    analytics_Storage: consent.analytics ? "granted" : "denied",
    ad_Storage: consent.marketing ? "granted" : "denied",
  });
}

// Apply a consent choice: start Clarity the first time analytics is accepted,
// otherwise just pass the updated choice to an already-running Clarity.
// If analytics was never accepted, nothing is loaded at all.
function applyConsent(consent: Consent): void {
  if (!started) {
    if (!consent.analytics) return;
    // init() injects the script and defines the window.clarity command queue,
    // so the consentV2 call below is queued safely even before the script loads
    Clarity.init(CLARITY_PROJECT_ID);
    started = true;
  }
  // Once started we never unload the script; a later "denied" is sent through
  // consentV2 so Clarity stops using analytics storage for this visitor.
  sendConsent(consent);
}

// Entry point, called once from main.tsx before the app renders.
export function startClarity(): void {
  // Gate 1: production hosts only; dev/localhost do nothing at all
  if (!PROD_HOSTS.has(window.location.hostname)) return;

  // Gate 2a: honour a choice the visitor already made (cookie from a past visit
  // or another xenquote.com page)
  const saved = readConsentCookie();
  if (saved) applyConsent(saved);

  // Gate 2b: react to choices made on this page via the shared banner
  window.addEventListener("xq-consent", (event: Event) => {
    const detail = (event as CustomEvent<ConsentEventDetail>).detail ?? {};
    applyConsent({ analytics: detail.analytics === true, marketing: detail.marketing === true });
  });
}
